<script setup lang="ts">
const i18nHead = useLocaleHead()
useHeadSafe({
  htmlAttrs: {
    lang: i18nHead.value.htmlAttrs!.lang,
    // @ts-ignore
    dir: i18nHead.value.htmlAttrs!.dir
  },
  link: [...(i18nHead.value.link || [])],
  meta: [...(i18nHead.value.meta || [])]
})

const layoutCustomProps = useAttrs()

const { status } = useAuth()
</script>

<template>
  <div
    :id="layoutCustomProps.id as string"
    :class="'landing-layout ' + (layoutCustomProps.class || '')"
  >
    <LandingNavBar v-if="status !== 'authenticated'" />
    <StudentNavbar v-else />

    <main :id="status === 'authenticated' ? 'auth-main' : ''">
      <slot />
    </main>

    <LandingFooter />
  </div>
</template>

<style lang="scss">
.landing-layout {
  // -------------------
  // LAYOUT
  // -------------------

  main {
    padding-top: var(--navbarHeight);
    min-height: calc(100vh - var(--navbarHeight));
  }

  #auth-main {
    padding-top: calc(var(--navbarHeight) + 20px);
  }

  // -------------------
  // SECTIONS
  // -------------------

  .section {
    $sectionMargin: 130px;

    padding: $sectionMargin 0;

    &-top {
      display: flex;
      justify-content: space-between;
      align-content: flex-end;

      p {
        width: 80%;
      }
    }

    &-title-center,
    &-title {
      position: relative;

      h2 {
        font-size: 2.5rem;
        line-height: 3.5rem;
      }

      h3 {
        font-size: 2rem;
        line-height: 2.7rem;
      }

      p {
        margin-top: 20px;
        color: var(--textMedium);
        width: 500px;
      }

      .btn-arrow {
        position: absolute;
        bottom: 0;
        right: 0;
      }
    }

    &-title-center {
      text-align: center;

      p {
        margin-right: auto;
        margin-left: auto;
        width: 60%;
      }
    }

    .slider {
      margin-top: 50px;
    }
  }

  .blue {
    background-color: var(--bgMedium);
  }

  // -------------------
  // GLOBAL
  // -------------------

  // -------------------
  // SECONDARY BAR
  // -------------------

  .secondary-bar {
    $border: 1px solid var(--borderLight);
    background-color: white;
    border-top: $border;
    border-bottom: $border;
    padding: 5px 0;
    position: sticky;
    top: var(--navbarHeight);
    left: 0;
    z-index: 10;
    right: 0;
    overflow-x: scroll;

    .container {
      display: flex;
    }

    .bar-item {
      padding: 20px 20px;
      border-radius: 10px;
      margin-right: 10px;
      font-size: 0.95rem;
      color: var(--darkBlue);
      position: relative;
      white-space: nowrap;
    }

    .bar-item::before {
      $height: 3px;
      position: absolute;
      content: '';
      bottom: -5px;
      right: 0;
      left: 0;
      margin: auto;
      width: 0;
      height: $height;
      border-radius: calc($height / 2);
      background-color: var(--purple);
      transition: width 0.4s ease-out;
    }

    .bar-item-active {
      color: var(--purple);
      font-weight: 500;
      position: relative;
    }

    .bar-item-active::before {
      width: 40%;
    }
  }

  @media (hover: hover) {
    .secondary-bar {
      .bar-item:hover {
        background-color: var(--lightPurple);
      }
    }
  }

  // -------------------
  // SLIDER
  // -------------------

  .small-screen-slider-btn {
    display: none;
  }

  // ---------------
  // RESPONSIVE
  // ---------------

  @media (max-width: 1300px) {
    .section {
      padding: 100px 0;
    }
  }

  @media (max-width: 900px) {
    .section {
      padding: 90px 0;
    }
  }

  @media (max-width: 600px) {
    .section {
      &-title-center,
      &-title {
        h2 {
          font-size: 6vw;
          line-height: 8vw;
        }

        h3 {
          font-size: 5.5vw;
          line-height: 8vw;
        }

        p {
          margin-top: 10px;
          width: 90%;
        }
      }
    }

    u::before {
      $margin-h: -5px;
      $margin-v: -3px;

      top: $margin-v;
      bottom: $margin-v;
      right: $margin-h;
      left: $margin-h;
    }

    .small-screen-slider-btn {
      $size: 60px;

      position: absolute;
      display: flex !important;
      align-items: center;
      justify-content: center;
      top: 0;
      bottom: 0;
      margin: auto;
      right: 10px;
      height: $size !important;
      width: $size !important;
      border-radius: 50% !important;
      background-color: white !important;
      box-shadow: 0 10px 30px rgba(141, 166, 182, 0.4);
      border: 1px solid whitesmoke;

      svg {
        $iconSize: 29%;
        height: $iconSize !important;
        width: $iconSize !important;
        fill: var(--black) !important;
      }
    }

    .secondary-bar {
      padding: 0;
      border-top: none;

      .bar-item {
        padding: 22px 17px 15px;
        border-bottom: 3px solid transparent;
        border-radius: 0;
        font-size: 0.9rem;
        color: var(--textLight);
        font-weight: 500;
      }

      .bar-item-active {
        border-color: var(--purple);
        color: var(--purple);
      }

      .bar-item-active::before {
        width: 0;
      }
    }
  }
}
</style>
